import { ReactNode, useEffect, useState } from 'react'
import { Paper, Box, styled, ClickAwayListener } from '@mui/material'
import { MapState } from '@carto/kepler.gl'
import { lngLatToXY } from 'features/builder/utils/mapUtils'
import useResizeObserver from 'features/common/ui/useResizeObserver'
import { theme } from '@carto/meridian-ds/theme'
import { MapContainerZIndex } from '../KeplerGl/MapContainer/constants'
import { COMMENT_MARKER_SIZE } from 'features/collaboration/ui/comments/CommentMarker'

export const MAP_POPOVER_WIDTH = 320

const POPOVER_MARGIN_VERT = theme.spacingValue * 2
const MIN_POPOVER_HEIGHT = 240

const PopoverRoot = styled(Box)(() => ({
  position: 'absolute',
  zIndex: MapContainerZIndex.MapCommentPopover,
  userSelect: 'text'
}))

const PopoverPaper = styled(Paper)(() => ({
  display: 'grid',
  gridTemplateRows: 'auto 1fr',
  padding: 0,
  margin: 0,
  width: `${MAP_POPOVER_WIDTH}px`,
  borderRadius: theme.spacingValue * 1
}))

type MapPopoverProps = {
  mapState: MapState
  position: { latitude: number; longitude: number }
  offset?: { x: number; y: number }
  children: ReactNode
  onClickAway?: (event: MouseEvent | TouchEvent) => void
  mapContainer?: HTMLElement | null
}

const emptyCallback = () => {
  /* */
}

export function getActualPopoverPosition({
  wantedPosition,
  popoverClientHeight,
  mapWidth,
  mapState,
  offset
}: {
  wantedPosition: { x: number; y: number }
  popoverClientHeight: number
  mapWidth: number // is it same as mapState.width?
  mapState: MapState
  offset?: { x: number; y: number }
}) {
  const offsetX = offset?.x ?? 0
  const offsetY = offset?.y ?? 0
  const { x, y } = wantedPosition
  const top = Math.round(Math.max(offsetY, y - popoverClientHeight + offsetY, POPOVER_MARGIN_VERT))

  let left = x + offsetX
  if (left + MAP_POPOVER_WIDTH > mapWidth) {
    left = x - MAP_POPOVER_WIDTH - 4
  }
  const maxTop = y - COMMENT_MARKER_SIZE - offsetY
  if (top > maxTop) {
    return {
      left: left,
      top: maxTop,
      maxHeight: Math.min(mapState?.height - maxTop - POPOVER_MARGIN_VERT, mapState?.height - POPOVER_MARGIN_VERT * 2)
    }
  }
  if (y + POPOVER_MARGIN_VERT / 2 > mapState.height - POPOVER_MARGIN_VERT) {
    return { left: left, top: top, maxHeight: y - POPOVER_MARGIN_VERT / 2 }
  }

  const maxHeight = Math.max(mapState?.height - top - POPOVER_MARGIN_VERT, MIN_POPOVER_HEIGHT)

  return { left: left, top: top, maxHeight }
}

function MapPopover({
  mapState,
  children,
  position,
  onClickAway = emptyCallback,
  offset,
  mapContainer
}: MapPopoverProps) {
  const [paperRef, setPaperRef] = useState<HTMLDivElement | null>(null)

  const screenPos = lngLatToXY(mapState, [position.longitude, position.latitude])
  const [{ left, top, maxHeight }, setPaperPos] = useState({ left: 0, top: 0, maxHeight: 0 })
  const updatePaperPos = () => {
    if (!screenPos || !mapContainer) return

    const allChildrenScrollHeight = Array.from(paperRef?.children ?? []).reduce(
      (acc, child) => acc + child.scrollHeight,
      0
    )
    setPaperPos(
      getActualPopoverPosition({
        wantedPosition: screenPos,
        popoverClientHeight: allChildrenScrollHeight ?? MIN_POPOVER_HEIGHT,
        mapWidth: mapContainer.clientWidth,
        mapState,
        offset
      })
    )
  }

  // Note: screenPos can't be in deps, as it causes infinite re-render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(updatePaperPos, [screenPos?.x, screenPos?.y, paperRef, children])
  useResizeObserver(paperRef, updatePaperPos)

  if (!screenPos) return null

  return (
    <PopoverRoot style={{ left, top }}>
      <ClickAwayListener onClickAway={onClickAway}>
        <PopoverPaper elevation={8} ref={setPaperRef} style={{ maxHeight: `${maxHeight}px` }}>
          {children}
        </PopoverPaper>
      </ClickAwayListener>
    </PopoverRoot>
  )
}

export default MapPopover
